import React, { useMemo, useRef, useState } from 'react'
import {
  Check,
  CheckCircle2,
  Clock,
  ExternalLink,
  Loader2,
  MessageSquarePlus,
  PencilLine,
  RotateCcw,
  Send,
  Undo2,
  X,
} from 'lucide-react'
import { FaInstagram, FaTiktok, FaYoutube } from 'react-icons/fa6'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'
import {
  useAddSubmissionNoteMutation,
  useGetSubmissionNotesQuery,
  useResolveSubmissionNoteMutation,
  useReviewSubmissionMutation,
  type Collaboration,
  type Milestone,
  type Submission,
  type SubmissionNote,
} from '@/redux/marketerApi'

type Decision = 'approved' | 'revision_requested' | null
type Asset = 'video' | 'caption'
type Entry = { video?: string; caption?: string | null; sameAsDeliverableId?: string }

const clock = (sec: number) => {
  const s = Math.max(0, Math.floor(sec))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}
const when = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

const PlatformIcon: React.FC<{ platform: string; className?: string }> = ({
  platform,
  className = 'h-5 w-5',
}) => {
  const p = platform.toLowerCase()
  if (p.includes('tiktok')) return <FaTiktok className={cn(className, 'text-[#111111]')} />
  if (p.includes('youtube')) return <FaYoutube className={cn(className, 'text-[#FF0000]')} />
  return <FaInstagram className={cn(className, 'text-[#E1306C]')} />
}

const Pill: React.FC<{ tone: 'blue' | 'green' | 'amber' | 'grey'; children: React.ReactNode }> = ({
  tone,
  children,
}) => (
  <span
    className={cn(
      'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold',
      tone === 'blue' && 'bg-[#E0EDFF] text-[#1D4ED8]',
      tone === 'green' && 'bg-[#E8F9D2] text-[#2F5E06]',
      tone === 'amber' && 'bg-[#FEF3D7] text-[#92400E]',
      tone === 'grey' && 'bg-[#F1F5F9] text-[#475569]',
    )}
  >
    {children}
  </span>
)

/** The video, with notes pinned to moments in it: markers on a strip, a list, and an add box */
const VideoReviewer: React.FC<{
  src: string
  notes: SubmissionNote[]
  general: SubmissionNote[]
  canNote: boolean
  onAdd: (timestampSec: number, text: string) => Promise<void>
  onResolve: (note: SubmissionNote) => void
}> = ({ src, notes, general, canNote, onAdd, onResolve }) => {
  const ref = useRef<HTMLVideoElement>(null)
  const [now, setNow] = useState(0)
  const [duration, setDuration] = useState(0)
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const pinned = notes
    .filter((n) => n.timestampSec !== null)
    .sort((a, b) => a.timestampSec! - b.timestampSec!)
  const seek = (t: number) => {
    if (!ref.current) return
    ref.current.currentTime = t
    ref.current.pause()
    setNow(t)
  }
  const add = async () => {
    if (!text.trim()) return
    setBusy(true)
    try {
      await onAdd(now, text.trim())
      setText('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
      <div className="min-w-0">
        <div className="overflow-hidden rounded-xl bg-[#0B0F14]">
          <video
            ref={ref}
            src={src}
            controls
            playsInline
            className="aspect-video w-full bg-[#0B0F14] object-contain"
            onTimeUpdate={(e) => setNow(e.currentTarget.currentTime)}
            onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
          />
        </div>
        {/* Notes along the video's length; tap one to jump there */}
        <div className="relative mt-3 h-6" aria-label="Notes on the timeline">
          <div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-border" />
          {duration ? (
            <div
              className="absolute top-1/2 h-1 -translate-y-1/2 rounded-full bg-[#84CC16]"
              style={{ width: `${Math.min(100, (now / duration) * 100)}%` }}
            />
          ) : null}
          {duration
            ? pinned.map((n, i) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => seek(n.timestampSec!)}
                  title={`${clock(n.timestampSec!)} ${n.text}`}
                  className={cn(
                    'absolute top-1/2 flex h-5 w-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-xs font-bold ring-2 ring-[#FFFFFF]',
                    n.isResolved ? 'bg-[#CBD5E1] text-[#334155]' : 'bg-[#16A34A] text-[#FFFFFF]',
                  )}
                  style={{ left: `${Math.min(100, (n.timestampSec! / duration) * 100)}%` }}
                >
                  {i + 1}
                </button>
              ))
            : null}
        </div>
      </div>

      <div className="flex min-w-0 flex-col rounded-xl border border-border">
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <p className="text-sm font-semibold text-foreground">
            Notes ({pinned.length + general.length})
          </p>
          <p className="text-xs text-muted-foreground">Pause anywhere to note that moment</p>
        </div>
        <ol className="max-h-72 flex-1 space-y-1 overflow-y-auto p-2">
          {general.map((n) => (
            <li
              key={n.id}
              className={cn(
                'flex gap-3 rounded-lg px-2 py-2 hover:bg-muted',
                n.isResolved && 'opacity-60',
              )}
            >
              <span className="h-fit shrink-0 rounded-md bg-[#F1F5F9] px-1.5 py-0.5 text-xs font-semibold text-[#334155]">
                General
              </span>
              <div className="min-w-0 flex-1">
                <p className={cn('text-sm text-foreground', n.isResolved && 'line-through')}>
                  {n.text}
                </p>
                <p className="text-xs text-muted-foreground">
                  {n.author?.name ?? 'You'} · {when(n.createdAt)}
                </p>
                {n.replies.map((r) => (
                  <p
                    key={r.id}
                    className="mt-1 border-l-2 border-border pl-2 text-xs text-foreground"
                  >
                    <span className="font-semibold">{r.author?.name}:</span> {r.text}
                  </p>
                ))}
              </div>
              <button
                type="button"
                onClick={() => onResolve(n)}
                title={n.isResolved ? 'Reopen' : 'Mark resolved'}
                className="h-fit shrink-0 rounded-md p-1 text-muted-foreground hover:bg-card hover:text-foreground"
              >
                {n.isResolved ? <Undo2 className="h-4 w-4" /> : <Check className="h-4 w-4" />}
              </button>
            </li>
          ))}
          {pinned.length ? (
            pinned.map((n, i) => (
              <li
                key={n.id}
                className={cn(
                  'group flex gap-3 rounded-lg px-2 py-2 hover:bg-muted',
                  n.isResolved && 'opacity-60',
                )}
              >
                <button
                  type="button"
                  onClick={() => seek(n.timestampSec!)}
                  className="h-fit shrink-0 rounded-md bg-[#F1F5F9] px-1.5 py-0.5 text-xs font-semibold tabular-nums text-[#334155] hover:bg-[#E2E8F0]"
                >
                  {i + 1} · {clock(n.timestampSec!)}
                </button>
                <div className="min-w-0 flex-1">
                  <p className={cn('text-sm text-foreground', n.isResolved && 'line-through')}>
                    {n.text}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {n.author?.name ?? 'You'} · {when(n.createdAt)}
                  </p>
                  {n.replies.map((r) => (
                    <p
                      key={r.id}
                      className="mt-1 border-l-2 border-border pl-2 text-xs text-foreground"
                    >
                      <span className="font-semibold">{r.author?.name}:</span> {r.text}
                    </p>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => onResolve(n)}
                  title={n.isResolved ? 'Reopen' : 'Mark resolved'}
                  className="h-fit shrink-0 rounded-md p-1 text-muted-foreground hover:bg-card hover:text-foreground"
                >
                  {n.isResolved ? <Undo2 className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                </button>
              </li>
            ))
          ) : general.length ? null : (
            <li className="px-2 py-6 text-center text-sm text-muted-foreground">
              No notes yet. Pause the video and add one at that moment.
            </li>
          )}
        </ol>
        {canNote ? (
          <div className="flex items-center gap-2 border-t border-border p-2">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onFocus={() => ref.current?.pause()}
              onKeyDown={(e) => e.key === 'Enter' && add()}
              placeholder={`Add a note at ${clock(now)}…`}
              aria-label="Note at the current time"
              className="h-10 min-w-0 flex-1 rounded-lg border border-border bg-card px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-[#84CC16]"
            />
            <span className="shrink-0 rounded-md bg-[#E8F9D2] px-2 py-1 text-xs font-semibold tabular-nums text-[#2F5E06]">
              {clock(now)}
            </span>
            <button
              type="button"
              onClick={add}
              disabled={busy || !text.trim()}
              aria-label="Add note"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#C0FD72] text-[#0F172A] disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}

/** The caption, the brand's suggested edits to it, and what each version said */
const CaptionReviewer: React.FC<{
  caption: string
  notes: SubmissionNote[]
  history: { version: number; caption: string | null; submittedAt: string; outcome: string }[]
  canNote: boolean
  onSuggest: (from: string, to: string) => Promise<void>
  onResolve: (note: SubmissionNote) => void
}> = ({ caption, notes, history, canNote, onSuggest, onResolve }) => {
  const [tab, setTab] = useState<'edits' | 'history'>('edits')
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(caption)
  const [busy, setBusy] = useState(false)
  const edits = notes.filter((n) => n.suggestion)
  const save = async () => {
    if (draft.trim() === caption.trim()) return setEditing(false)
    setBusy(true)
    try {
      await onSuggest(caption, draft.trim())
      setEditing(false)
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="space-y-3">
      {editing ? (
        <div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={4}
            aria-label="Suggested caption"
            className="w-full resize-none rounded-xl border border-[#84CC16] bg-card px-4 py-3 text-base text-foreground outline-none ring-4 ring-[#C0FD72]/30"
          />
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setDraft(caption)
                setEditing(false)
              }}
              className="h-9 rounded-lg border border-border px-3 text-sm font-medium text-foreground hover:bg-muted"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-[#C0FD72] px-3 text-sm font-semibold text-[#0F172A]"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save suggestion
            </button>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-[#FAFAF9] px-4 py-3 text-base leading-relaxed text-foreground whitespace-pre-wrap">
          {caption || <span className="text-muted-foreground">No caption with this version.</span>}
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-1 rounded-lg bg-muted p-0.5 text-sm">
          {(['edits', 'history'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={cn(
                'rounded-md px-3 py-1 font-medium',
                tab === t ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground',
              )}
            >
              {t === 'edits' ? `Suggested edits (${edits.length})` : 'Version history'}
            </button>
          ))}
        </div>
        {canNote && !editing && caption ? (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#3F7A0A] hover:underline"
          >
            <PencilLine className="h-4 w-4" /> Suggest an edit
          </button>
        ) : null}
      </div>

      {tab === 'edits' ? (
        edits.length ? (
          <ul className="space-y-2">
            {edits.map((n) => (
              <li
                key={n.id}
                className={cn(
                  'rounded-xl border border-border p-3 text-sm',
                  n.isResolved && 'opacity-60',
                )}
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {n.isResolved ? 'Resolved' : 'Suggested'} · {n.author?.name ?? 'You'}
                </p>
                <p className="mt-1 text-foreground whitespace-pre-wrap">{n.suggestion!.to}</p>
                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={() => onResolve(n)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
                  >
                    {n.isResolved ? (
                      <>
                        <RotateCcw className="h-3.5 w-3.5" /> Reopen
                      </>
                    ) : (
                      <>
                        <X className="h-3.5 w-3.5" /> Withdraw
                      </>
                    )}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">
            Suggest wording and it goes to the athlete with your review.
          </p>
        )
      ) : (
        <ol className="space-y-2">
          {history.map((h) => (
            <li key={h.version} className="rounded-xl border border-border p-3 text-sm">
              <p className="text-xs font-semibold text-muted-foreground">
                Version {h.version} · {when(h.submittedAt)} · {h.outcome}
              </p>
              <p className="mt-1 text-foreground whitespace-pre-wrap">{h.caption || '—'}</p>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}

const DecisionButtons: React.FC<{
  label: string
  value: Decision
  onChange: (d: Decision) => void
  disabled?: boolean
}> = ({ label, value, onChange, disabled }) => (
  <div className="grid grid-cols-2 gap-2">
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(value === 'approved' ? null : 'approved')}
      aria-pressed={value === 'approved'}
      className={cn(
        'inline-flex h-11 items-center justify-center gap-2 rounded-xl font-semibold transition-colors disabled:opacity-50',
        value === 'approved'
          ? 'bg-[#16A34A] text-[#FFFFFF]'
          : 'bg-[#C0FD72] text-[#0F172A] hover:bg-[#B2F25E]',
      )}
    >
      <Check className="h-4 w-4" />{' '}
      {value === 'approved' ? `${label} approved` : `Approve ${label.toLowerCase()}`}
    </button>
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(value === 'revision_requested' ? null : 'revision_requested')}
      aria-pressed={value === 'revision_requested'}
      className={cn(
        'inline-flex h-11 items-center justify-center gap-2 rounded-xl border font-semibold transition-colors disabled:opacity-50',
        value === 'revision_requested'
          ? 'border-[#EA580C] bg-[#FFF1E6] text-[#C2410C]'
          : 'border-[#FDBA8C] bg-card text-[#C2410C] hover:bg-[#FFF7ED]',
      )}
    >
      <RotateCcw className="h-4 w-4" /> Request changes
    </button>
  </div>
)

/** The athlete's content for review: one deliverable at a time, video notes, caption edits */
export const ContentReview: React.FC<{
  collaboration: Collaboration
  milestone: Milestone
  athleteName: string
  onReviewed: () => void
}> = ({ collaboration: c, milestone: m, athleteName, onReviewed }) => {
  const { toast } = useToast()
  const first = athleteName.split(' ')[0]
  const versions = useMemo(() => [...m.submissions].sort((a, b) => b.version - a.version), [m])
  const latest = versions[0]
  const [versionId, setVersionId] = useState<string | null>(null)
  const sub: Submission | undefined = versions.find((s) => s.id === versionId) ?? latest
  const isLatestPending = !!sub && sub.id === latest?.id && sub.status === 'pending_review'

  const entries = ((sub?.contentUrls as any)?.deliverables ?? null) as Record<string, Entry> | null
  const deliverables = (c.structuredDeliverables ?? []).slice().sort((a, b) => a.order - b.order)
  const [selected, setSelected] = useState<string | null>(null)
  const current = deliverables.find((d) => d.id === selected) ?? deliverables[0]
  const resolveEntry = (id: string): Entry | undefined => {
    const e = entries?.[id]
    return e?.sameAsDeliverableId ? entries?.[e.sameAsDeliverableId] : e
  }
  const entry = current ? resolveEntry(current.id) : undefined

  const { data: notes = [] } = useGetSubmissionNotesQuery(
    { collaborationId: c.id, submissionId: sub?.id ?? '' },
    { skip: !sub },
  )
  const [addNote] = useAddSubmissionNoteMutation()
  const [resolveNote] = useResolveSubmissionNoteMutation()
  const [review, { isLoading: sending }] = useReviewSubmissionMutation()
  const [decisions, setDecisions] = useState<Record<string, Partial<Record<Asset, Decision>>>>({})
  const [extra, setExtra] = useState<Record<string, string>>({})

  const forDeliverable = (id: string, target: Asset) =>
    notes.filter((n) => n.target === target && (n.deliverableId ?? deliverables[0]?.id) === id)
  // Comments not pinned to anything (older ones, or from the athlete's side)
  const general = notes.filter((n) => n.target === 'general')

  const onResolve = (n: SubmissionNote) =>
    sub &&
    resolveNote({
      collaborationId: c.id,
      submissionId: sub.id,
      noteId: n.id,
      resolved: !n.isResolved,
    })

  // Which deliverables in this version need a decision, and whether each has one
  const reviewable = deliverables.filter(
    (d) => entries?.[d.id] && !entries[d.id].sameAsDeliverableId,
  )
  const needs = reviewable.flatMap((d) => {
    const e = resolveEntry(d.id)
    return [
      { id: d.id, asset: 'video' as const },
      ...(e?.caption ? [{ id: d.id, asset: 'caption' as const }] : []),
    ]
  })
  const decided = needs.filter((n) => decisions[n.id]?.[n.asset])
  const feedbackFor = (id: string, asset: Asset) => {
    const open = forDeliverable(id, asset).filter((n) => !n.isResolved)
    const lines = open.map((n) =>
      n.suggestion
        ? `Suggested caption: ${n.suggestion.to}`
        : n.timestampSec !== null
          ? `${clock(n.timestampSec)} ${n.text}`
          : n.text,
    )
    const typed = extra[`${id}:${asset}`]?.trim()
    return [...lines, ...(typed ? [typed] : [])].join('\n')
  }
  const missingFeedback = needs.filter(
    (n) => decisions[n.id]?.[n.asset] === 'revision_requested' && !feedbackFor(n.id, n.asset),
  )

  const send = async () => {
    const payload: Record<string, any> = {}
    for (const d of reviewable) {
      const e = resolveEntry(d.id)
      payload[d.id] = {
        // Notes go to the athlete as the feedback on whatever needs changing
        video: {
          status: decisions[d.id]?.video,
          comment:
            decisions[d.id]?.video === 'revision_requested' ? feedbackFor(d.id, 'video') : '',
        },
        ...(e?.caption
          ? {
              caption: {
                status: decisions[d.id]?.caption,
                comment:
                  decisions[d.id]?.caption === 'revision_requested'
                    ? feedbackFor(d.id, 'caption')
                    : '',
              },
            }
          : {}),
      }
    }
    try {
      await review({
        collaborationId: c.id,
        milestoneId: m.id,
        action: needs.every((n) => decisions[n.id]?.[n.asset] === 'approved')
          ? 'approve'
          : 'request_changes',
        reviewNotes: { deliverables: payload },
      }).unwrap()
      toast({
        title: needs.every((n) => decisions[n.id]?.[n.asset] === 'approved')
          ? 'Approved'
          : `Feedback sent to ${first}`,
        description: needs.every((n) => decisions[n.id]?.[n.asset] === 'approved')
          ? `${first} can move on to posting.`
          : `${first} will upload a new version with your notes.`,
      })
      setDecisions({})
      setExtra({})
      onReviewed()
    } catch (err: any) {
      toast({
        title: "Couldn't send the review",
        description: err?.data?.error ?? 'Try again in a moment.',
        variant: 'destructive',
      })
    }
  }

  if (!sub) {
    return (
      <div className="rounded-xl border border-dashed border-border px-6 py-10 text-center">
        <Clock className="mx-auto h-6 w-6 text-muted-foreground" />
        <p className="mt-2 font-semibold text-foreground">Waiting for {first}'s first version</p>
        <p className="text-sm text-muted-foreground">
          {m.dueDate
            ? `Due ${new Date(m.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}. `
            : ''}
          You'll be notified when it arrives.
        </p>
      </div>
    )
  }

  // Link-based submissions (a gifting post, a licensing draft): approve or ask for changes
  if (!entries) {
    const links = Array.isArray(sub.contentUrls)
      ? sub.contentUrls
      : (((sub.contentUrls as any)?.links ?? []) as string[]).map((url) => ({ url, name: url }))
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Version {sub.version} · {when(sub.submittedAt)}
        </p>
        {sub.notes ? <p className="text-foreground">{sub.notes}</p> : null}
        <ul className="space-y-1">
          {links.map((l: any) => (
            <li key={l.url}>
              <a
                href={l.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-[#2563EB] hover:underline"
              >
                <ExternalLink className="h-4 w-4" /> {l.name || l.url}
              </a>
            </li>
          ))}
        </ul>
        {isLatestPending ? (
          <LinkDecision
            collaborationId={c.id}
            milestoneId={m.id}
            first={first}
            onDone={onReviewed}
          />
        ) : null}
      </div>
    )
  }

  const status = (id: string) => {
    const r = (sub.reviewNotes as any)?.deliverables?.[id]
    if (!entries[id]) return <Pill tone="grey">Not started</Pill>
    if (sub.status === 'pending_review') return <Pill tone="blue">In review</Pill>
    if (r?.video?.status === 'revision_requested' || r?.caption?.status === 'revision_requested')
      return <Pill tone="amber">Changes requested</Pill>
    return <Pill tone="green">Approved</Pill>
  }

  const history = versions.map((v) => {
    const e = ((v.contentUrls as any)?.deliverables ?? {})[current?.id ?? ''] as Entry | undefined
    return {
      version: v.version,
      caption: e?.caption ?? null,
      submittedAt: v.submittedAt,
      outcome:
        v.status === 'pending_review'
          ? 'In review'
          : v.status === 'approved'
            ? 'Approved'
            : 'Changes requested',
    }
  })

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {deliverables.map((d, i) => (
            <button
              key={d.id}
              type="button"
              onClick={() => setSelected(d.id)}
              aria-pressed={current?.id === d.id}
              className={cn(
                'flex items-center gap-3 rounded-xl border px-3 py-2 text-left',
                current?.id === d.id
                  ? 'border-[#9BD85A] bg-[#F4FBEA]'
                  : 'border-border bg-card hover:bg-muted',
              )}
            >
              <PlatformIcon platform={d.platform} />
              <span>
                <span className="block text-sm font-semibold text-foreground">
                  #{i + 1} {d.platform} {d.format}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {d.notes || 'Deliverable'}
                </span>
              </span>
              {status(d.id)}
            </button>
          ))}
        </div>
        {versions.length > 1 ? (
          <select
            value={sub.id}
            onChange={(e) => setVersionId(e.target.value)}
            aria-label="Version"
            className="h-9 rounded-lg border border-border bg-card px-3 text-sm text-foreground"
          >
            {versions.map((v) => (
              <option key={v.id} value={v.id}>
                Version {v.version} ·{' '}
                {new Date(v.submittedAt).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                })}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-sm text-muted-foreground">
            Version {sub.version} · Submitted {when(sub.submittedAt)}
          </span>
        )}
      </div>

      {current && entry?.video ? (
        <>
          <section className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h4 className="font-semibold text-foreground">Video</h4>
              {isLatestPending ? null : (
                <span className="text-xs text-muted-foreground">Read only: older version</span>
              )}
            </div>
            <VideoReviewer
              src={entry.video}
              notes={forDeliverable(current.id, 'video')}
              general={general}
              canNote={isLatestPending}
              onResolve={onResolve}
              onAdd={async (t, text) => {
                await addNote({
                  collaborationId: c.id,
                  submissionId: sub.id,
                  text,
                  metadata: { target: 'video', deliverableId: current.id, timestampSec: t },
                }).unwrap()
              }}
            />
            {isLatestPending ? (
              <DecisionButtons
                label="Video"
                value={decisions[current.id]?.video ?? null}
                onChange={(d) =>
                  setDecisions((x) => ({ ...x, [current.id]: { ...x[current.id], video: d } }))
                }
              />
            ) : null}
            {decisions[current.id]?.video === 'revision_requested' &&
            !forDeliverable(current.id, 'video').some((n) => !n.isResolved) ? (
              <textarea
                value={extra[`${current.id}:video`] ?? ''}
                onChange={(e) =>
                  setExtra((x) => ({ ...x, [`${current.id}:video`]: e.target.value }))
                }
                rows={2}
                placeholder={`What should ${first} change? Or add notes on the video above.`}
                className="w-full resize-none rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground outline-none focus:border-[#84CC16]"
              />
            ) : null}
          </section>

          {entry.caption !== undefined && entry.caption !== null ? (
            <section className="space-y-3 border-t border-border pt-4">
              <h4 className="font-semibold text-foreground">Caption</h4>
              <CaptionReviewer
                caption={entry.caption ?? ''}
                notes={forDeliverable(current.id, 'caption')}
                history={history}
                canNote={isLatestPending}
                onResolve={onResolve}
                onSuggest={async (from, to) => {
                  await addNote({
                    collaborationId: c.id,
                    submissionId: sub.id,
                    text: '',
                    metadata: {
                      target: 'caption',
                      deliverableId: current.id,
                      suggestion: { from, to },
                    },
                  }).unwrap()
                }}
              />
              {isLatestPending ? (
                <DecisionButtons
                  label="Caption"
                  value={decisions[current.id]?.caption ?? null}
                  onChange={(d) =>
                    setDecisions((x) => ({ ...x, [current.id]: { ...x[current.id], caption: d } }))
                  }
                />
              ) : null}
              {decisions[current.id]?.caption === 'revision_requested' &&
              !forDeliverable(current.id, 'caption').some((n) => !n.isResolved) ? (
                <textarea
                  value={extra[`${current.id}:caption`] ?? ''}
                  onChange={(e) =>
                    setExtra((x) => ({ ...x, [`${current.id}:caption`]: e.target.value }))
                  }
                  rows={2}
                  placeholder="What should change? Or suggest an edit above."
                  className="w-full resize-none rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground outline-none focus:border-[#84CC16]"
                />
              ) : null}
            </section>
          ) : null}
        </>
      ) : (
        <p className="rounded-xl border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
          {first} hasn't sent this deliverable in version {sub.version}.
        </p>
      )}

      {isLatestPending ? (
        <div className="sticky bottom-3 z-10 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#9BD85A] bg-[#F4FBEA] px-4 py-3 shadow-lg">
          <p className="text-sm text-foreground">
            <CheckCircle2 className="mr-1 inline h-4 w-4 text-[#16A34A]" />
            {decided.length} of {needs.length} decided
            {missingFeedback.length
              ? ' · add a note for each change you request'
              : decided.length < needs.length
                ? ' · approve or request changes on each video and caption'
                : ''}
          </p>
          <button
            type="button"
            onClick={send}
            disabled={sending || decided.length < needs.length || missingFeedback.length > 0}
            className="inline-flex h-11 items-center gap-2 rounded-xl bg-[#0F172A] px-5 font-semibold text-[#FFFFFF] disabled:opacity-40"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <MessageSquarePlus className="h-4 w-4" />
            )}
            Send review to {first}
          </button>
        </div>
      ) : null}
    </div>
  )
}

/** Approve or ask for changes on a link-based submission */
export const LinkDecision: React.FC<{
  collaborationId: string
  milestoneId: string
  first: string
  onDone: () => void
}> = ({ collaborationId, milestoneId, first, onDone }) => {
  const { toast } = useToast()
  const [review, { isLoading }] = useReviewSubmissionMutation()
  const [note, setNote] = useState('')
  const go = async (action: 'approve' | 'request_changes') => {
    if (action === 'request_changes' && !note.trim()) {
      toast({ title: `Tell ${first} what to change`, variant: 'destructive' })
      return
    }
    try {
      await review({ collaborationId, milestoneId, action, reviewNotes: note.trim() }).unwrap()
      toast({ title: action === 'approve' ? 'Approved' : `Feedback sent to ${first}` })
      setNote('')
      onDone()
    } catch (err: any) {
      toast({ title: "Couldn't send", description: err?.data?.error, variant: 'destructive' })
    }
  }
  return (
    <div className="space-y-2">
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        placeholder="A note for the athlete (needed when you request changes)"
        className="w-full resize-none rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground outline-none focus:border-[#84CC16]"
      />
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          disabled={isLoading}
          onClick={() => go('approve')}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[#C0FD72] font-semibold text-[#0F172A]"
        >
          <Check className="h-4 w-4" /> Approve
        </button>
        <button
          type="button"
          disabled={isLoading}
          onClick={() => go('request_changes')}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-[#FDBA8C] font-semibold text-[#C2410C]"
        >
          <RotateCcw className="h-4 w-4" /> Request changes
        </button>
      </div>
    </div>
  )
}

export { PlatformIcon, Pill }
